import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import { BaseModuleProps } from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import { getActiveColorScheme, getTranslations } from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import Typography from "../Typography.js";
import TypographyContent from "../TypographyContent.js";
import TypographyWrapper from "../TypographyWrapper.js";
import TypographyWrapperInner from "../TypographyWrapperInner.js";

export const translations = {
  de: {
    documentTitle: "Seite nicht gefunden",
    title: "Die von Ihnen gesuchte Seite wurde nicht gefunden",
    text: (email?: string) => (
      <>
        <p>
          Leider können wir kein Resultat für Ihre Suchanfrage finden – Ihren
          Urlaub zu einem besonderen Erlebnis machen können wir sehr wohl.
        </p>
        <p>
          Interessiert? Wir machen Ihnen gerne ein individuelles Angebot:{" "}
          <a href={"mailto:" + email}>Anfrage senden</a>
        </p>
        <p>
          Sie können auch gerne die Navigation über die Menüleiste verwenden um
          zur gewünschten Seite zurückzukehren.
        </p>
      </>
    ),
  },
  it: {
    documentTitle: "Pagina non trovata",
    title: "La pagina da Lei richiesta non è stata trovata",
    text: (email?: string) => (
      <>
        <p>
          Purtroppo non siamo in grado di trovare risultati per la ricerca da
          Lei effettuata – ma siamo in grado di rendere la Sua vacanza
          indimenticabile.
        </p>{" "}
        <p>
          Le interessa? Siamo felici di proporLe un’offerta individuale:{" "}
          <a href={"mailto:" + email}>Invia richiesta</a>
        </p>
        <p>
          Per tornare alla pagina precedente, può servirsi degli strumenti di
          navigazione nella barra del menu.
        </p>
      </>
    ),
  },
  en: {
    documentTitle: "Page not found",
    title: "The page you were looking for could not be found ",
    text: (email?: string) => (
      <>
        <p>
          Unfortunately we could not find any results matching your search – but
          we can make your holiday unforgettable.
        </p>{" "}
        <p>
          Are you interested? We will be glad to send you an individual offer:{" "}
          <a href={"mailto:" + email}>Send request</a>
        </p>
        <p>
          To go back to previous page you can also use the navigation buttons on
          the menu bar.
        </p>
      </>
    ),
  },
};

const NotFoundTextModule: FunctionComponent<BaseModuleProps> = ({
  translatedModule: {
    id,
    translation: { languageId },
  },
  translatedModule,
}) => {
  const i18n = getTranslations(languageId, translations);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const email = useAppSelector(
    (state) => state.accommodation[languageId]?.email ?? undefined,
  );

  return (
    <Typography
      title={i18n.title}
      subtitle={undefined}
      id={id}
      colors={{ background: scheme.main.background }}
    >
      <TypographyWrapper
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--default-padding"],
        )}
      >
        <TypographyWrapperInner>
          <TypographyContent className={css({ color: scheme.main.text })}>
            <div>
              <ModuleHeadings
                scheme={scheme}
                isFirstOnPage={true}
                textAlign={{ title: "center", subtitle: "center" }}
                title={i18n.title}
                subtitle={undefined}
              />
              <section
                className={ClassNames(
                  moduleBaseStyles.Module__BodyText,
                  "TextAlign--center",
                )}
              >
                {i18n.text(email)}
              </section>
            </div>
          </TypographyContent>
        </TypographyWrapperInner>
      </TypographyWrapper>
    </Typography>
  );
};

export default NotFoundTextModule;
